import { useRef, useEffect } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import useFetchAndSetModelUrl from "../../utils/useFetchAndSetModelUrl";

const ModelViewer = ({ source_media, mediaRef }) => {
  const mountRef = useRef(null);
  const { model, modelType, textureUrls } =
    useFetchAndSetModelUrl(source_media);

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(
      mountRef.current.clientWidth,
      mountRef.current.clientHeight
    );
    mountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5).normalize();
    scene.add(directionalLight);

    const loadTextures = async (textureUrls) => {
      const textureLoader = new THREE.TextureLoader();
      const textures = await Promise.all(
        textureUrls.map(
          (url) =>
            new Promise((resolve) => {
              textureLoader.load(
                url,
                (texture) => resolve(texture),
                undefined,
                () => resolve(null)
              );
            })
        )
      );
      return textures;
    };

    const applyTexturesToModel = (model, textures) => {
      let hasMaterials = false;
      const textureLoader = new THREE.TextureLoader();
      const loadedTextures = textures.map((url) => textureLoader.load(url));

      model.traverse((child) => {
        if (child.isMesh) {
          hasMaterials = true;
          if (loadedTextures.length > 0) {
            child.material.map =
              loadedTextures[child.materialIndex] || loadedTextures[0];
          } else {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xffffff,
            });
          }

          child.material.needsUpdate = true;
        }
      });

      if (!hasMaterials) {
        console.warn("No materials found, setting background color.");
        scene.background = new THREE.Color(0x333333);
      } else {
        scene.background = null;
      }
    };

    const loadModel = async () => {
      const textures = await loadTextures(textureUrls);

      const fitModelToScene = (model) => {
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        model.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const cameraZ = maxDim / (2 * Math.tan(fov / 2));

        camera.position.set(0, 0, cameraZ * 1.5);
        camera.near = maxDim / 100;
        camera.far = maxDim * 100;
        camera.updateProjectionMatrix();

        controls.target.set(0, 0, 0);
        controls.update();
      };

      if (modelType === "glb") {
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(model, (gltf) => {
          fitModelToScene(gltf.scene);
          scene.add(gltf.scene);
          animate();
        });
      } else if (modelType === "fbx") {
        const fbxLoader = new FBXLoader();
        fbxLoader.load(model, (fbx) => {
          applyTexturesToModel(fbx, textures);
          fitModelToScene(fbx);
          scene.add(fbx);
          animate();
        });
      } else if (modelType === "obj" || modelType === "stl") {
        applyTexturesToModel(model, textures);
        fitModelToScene(model);
        scene.add(model);
        animate();
      }
    };

    if (model) {
      loadModel();
    }

    let isAnimating = true;

    const animate = () => {
      if (!isAnimating) return;
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      isAnimating = false;
      controls.dispose();
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [model, modelType, textureUrls]);

  return (
    <div
      ref={mediaRef}
      style={{
        position: "relative",
        top: "auto",
        left: "auto",
        width: "96vw",
        height: "96vw",

        borderRadius: "20px",
        marginBottom: 10,
      }}
    >
      <div
        ref={mountRef}
        style={{
          width: "90vw",
          height: "100%",
          borderRadius: "20px",
          overflow: "hidden",
        }}
      />
    </div>
  );
};

export default ModelViewer;
